/***********************************************************************************************************************
 *
 * Version 1.0.0
 *
 * User: eliranm
 * Date: 22/10/14
 *
 * ---
 *
 *
 * Uploads files to the backend using the specified URL. This component depends on the File Upload transport,
 * which uses an injected iframe loaded from the backend, to send the file via a form with an input of type 'file'.
 *
 *
 * Usage
 * =====
 *
 * # Simple use:
 *
 *  var FileUploadInput = require('ui/upload/FileUploadInput');
 *  ...
 *  uiComponents: {
 *      fileUploadInput: {
 *          type: FileUploadInput,
 *          options: {
 *              el: 'view.ui.fileUploadInput',
 *              uploadUrl: LEResourceResolver.getUrl(LEResourceResolver.apiResources.AccountConfig.Import)
 *          }
 *      }
 *  }
 *
 *
 * # Extended use:
 *
 *  var FileUploadInput = require('ui/upload/FileUploadInput');
 *  ...
 *  uiComponents: {
 *      fileUploadInput: {
 *          type: FileUploadInput,
 *          options: {
 *              el: 'view.ui.fileUploadInput',
 *              permutation: FileUploadInput.PERMUTATION.CUSTOM,
 *              uploadUrl: LEResourceResolver.getUrl(LEResourceResolver.apiResources.AccountConfig.Import),
 *              classNames: [
 *                  'foo', 'bar'
 *              ],
 *              buttonText: 'Choose File...',
 *              buttonStyle: 'background: blue; border: 1px solid orange;',
 *              iFrameTitle: 'Upload Service',
 *              uploadTimeout: 1000 * 60 * 5,
 *              uploadExtensions: [
 *                  'png', 'gif'
 *              ],
 *              iFrameLoadTimeout: 1000 * 5,
 *              errorPosition: FileUploadInput.ERROR_POSITION.RIGHT,
 *              sessionToken: LE.sessionManager.getGlob()
 *          }
 *      }
 *  }
 *  ...
 *  onRender: function () {
 *      this.listenTo(this.uiComponents.fileUploadInput, FileUploadInput.EVE.fileSelectionChange, this._onFileSelectionChange);
 *  }
 *
 *
 * Options
 * =======
 *
 *  permutation {String} (optional)
 *      A string to represent the component's flavor, inspect FileUploadInput.PERMUTATION for available values. By default,
 *      the component initializes with the DEFAULT permutation, which means it will have the LiveEngage look and feel,
 *      and will have inline level flow.
 *      When using the CUSTOM permutation, the buttonStyle option may be used to style the file input button.
 *
 *  uploadUrl {String} (mandatory)
 *      A URL that points to an API endpoint (possibly with query params) which will be set as the
 *      iframe's form action.
 *
 *  classNames {Array} (optional)
 *      An array of CSS classnames to be assigned to the wrapping container.
 *
 *  uploadExtensions {Array} (optional)
 *      An array of file extensions to filter the allowed file types in the browser's 'Open file' dialog.
 *
 *  buttonText {String} (optional)
 *      A text for the file input's button. If not passed, the component's $el text is used.
 *
 *  buttonStyle {String} (optional)
 *      NOTE: This option only takes effect when using the CUSTOM permutation.
 *      Inline style for the file input's button (it cannot be styled using stylesheets nor can a
 *      reference be obtained for the button's object, as it resides within an iframe with its src set to another domain).
 *
 *  iFrameTitle {String} (optional)
 *      The iframe's title attribute value.
 *
 *  uploadTimeout {Number} (optional)
 *      A timeout for the file upload, in milliseconds.
 *
 *  iFrameLoadTimeout {Number} (optional)
 *      A timeout for loading the iframe, in milliseconds.
 *
 *  errorPosition {String} (optional)
 *      @since 1.1.0
 *      The position of the error message bubble. Use values from the FileUploadInput.ERROR_POSITION enumeration, default is
 *      FileUploadInput.ERROR_POSITION.TOP.
 *
 *
 * API
 * ===
 *
 *  startUpload()
 *      Posts an upload request to the backend using the endpoint and the selected file.
 *
 *  isFileSelected()
 *      @since 1.1.0
 *      Checks if a file is currently selected.
 *
 *  setError(msg)
 *      @since 1.1.0
 *      Sets an error message to be shown in a bubble and applies a red border around the component.
 *
 *  removeError()
 *      @since 1.1.0
 *      Removes the error message bubble and the red border around the component.
 *
 *
 * Events
 * ======
 *
 * Events are triggered on the following:
 *
 * - file selection change
 * - file upload start
 * - file upload complete
 * - file upload success
 * - file upload error
 *
 * Event names for registration are available under the FileUploadInput.EVE namespace.
 *
 * @see assets/ui-components/widgets/file-upload-input/config/fileUploadInputConfig.json
 *
 *
 * Component Dependencies
 * ======================
 *
 * - file upload transport (window.lpTag.taglets.fileupload)
 * - Bubble ui component (@since 1.1.0)
 *
 *
 ***********************************************************************************************************************
 *
 * Version 1.1.0
 *
 * User: eliranm
 * Date: 12/11/14
 *
 * ---
 *
 * Change log:
 *
 * - File name label added.
 * - Added API for setting/removing errors.
 * - Added option to control the error bubble position.
 *
 *
 ***********************************************************************************************************************
 */
define(function (require) {
    'use strict';

    var LE = require('app');
    var _ = require('underscore');
    var Marionette = require('marionette');
    var Logger = require('logger');
    var Config = require('ui/file-upload-input/config/fileUploadInputConfig.json');
    var Utils = require('ui/file-upload-input/js/FileUploadInputUtils');
    var template = require('ui/file-upload-input/templates/fileUploadInputTemplate.tmpl');
    var LEResourceResolver = require('leResourceResolver');
    var FileUploadInputModelFactory = require('ui/file-upload-input/js/FileUploadInputModelFactory');
    var Bubble = require('ui/bubble/Bubble');
    var Loader = require('ui/loader/Loader');

    var BUTTON_ON_RIGHT_CLASSNAME = "button-on-right";
    var NO_TEXT_BUTTON_CLASSNAME = "no-text";

    var FileUploadInput = Marionette.ItemView.extend({
        FILE_INPUT_STATES: Config.FILE_INPUT_STATES,
        version: '1.1.0',
        template: template,
        withUIComponents: true,
        errorOptions: null,
        errorBubbleTarget: null,

        ui: {
            wrapper: '.' + Config.wrapperBaseClassName,
            frameContainer: '.' + Config.fileUploadTransport.frameContainerClassName,
            uploadLabel: '.' + Config.fileLabelClassName
        },

        events: _.invert({
            '_showErrorBubble': 'mouseenter .' + Config.errorClassName,
            '_hideErrorBubble': 'mouseleave .' + Config.errorClassName
        }),


        // overridden

        initialize: function (options) {
            this.sessionToken = options.sessionToken;
            this.logger = Logger.getLogger(Config.loggerNamespace);
            // todo - detect pointer events (with modernizr), set mode (native/not), use button ui-component
            this._validateDependencies();
            this._initModel(options);
            this.ui.frameContainer = !_.isUndefined(this.model.get("frameContainerClassName")) ? '.' + this.model.get("frameContainerClassName") : '.' + Config.fileUploadTransport.frameContainerClassName;
            this._initFileUpload();
        },

        onRender: function () {
            this._setErrorBubbleTargetElement();
            this._setFileUploadLabelProperties();
            this._showIFrameLoader();
            this._setPermutationClasses();
        },

        onClose: function () {
            if (this.uploadClient) {
                this.uploadClient.removeUpload(); // removes iframe from dom
            }
            this._destroyError();
        },


        // public

        /**
         * Trigger the upload.
         */
        startUpload: function () {
            this.logger.info('initiating file upload', 'FileUploadInput:startUpload', this.uploadClient);
            if (this.uploadClient) {
                this.uploadClient.startUpload();
            }
        },

        /**
         * Checks if a file is selected.
         * @returns {Boolean} <code>true</code> if a file is selected, <code>false</code> otherwise.
         * @since 1.1.0
         */
        isFileSelected: function () {
            if (this.uploadClient) {
                return this.uploadClient.isFileSelected();
            }
        },

        /**
         * Sets an error message to be shown in a bubble and paints a red border around the component. The error bubble
         * position is controlled by the <code>errorPosition</code> option (see the main docs).
         * The error is persisted until the it is removed using <code>FileUploadInput.removeError()</code>.
         * This function can be used to remove the error as well, by using no arguments, i.e.
         * <code>FileUploadInput.setError()</code>, or passing a falsey value, i.e. <code>FileUploadInput.setError(0)</code>.
         * @param msg The message text.
         * @since 1.1.0
         */
        setError: function (msg) {
            if (!msg) {
                return this._destroyError();
            }
            if (!_.isString(msg)) {
                this.logger.error('msg must be of type String to set an upload error notification', 'FileUploadInput:setError',{msg: msg});
                this.logger.graph({ name: "UIComponents:FileUploadInput:setError:invalidMsg"});
                return;
            }
            this._destroyError();
            this._initError(msg);
        },

        /**
         * Removes the currently set error.
         * @returns {Boolean} <code>true</code> if the error was removed, <code>false</code> otherwise.
         * @since 1.1.0
         */
        removeError: function () {
            if (this._isErrorOptionsValid()) {
                return this._destroyError();
            }
        },

        setFileInputLabelNoFileText: function(){
            this.ui.uploadLabel.text(this.model.getNoFileChosenMessage());
        },

        setFileInputLabelText: function(text){
            this.ui.uploadLabel.text(text);
        },

        setFileInputLabelState: function(state){
            if (state === Config.FILE_INPUT_STATES.NON_EXISITING_FILE) {
                this.ui.uploadLabel.addClass(Config.noFileChosenClassName);
            }
            else {
                this.ui.uploadLabel.removeClass(Config.noFileChosenClassName);
            }
        },
        resetUpload: function(){
            this.uploadClient.removeUpload();
            this._initFileUpload();
        },
        updateUploadUrl: function(url){
            this.model.setUploadUrl(url);
            if (this.uploadClient) {
                this.uploadClient.changeFormAttributes({url:url});
            }
        },

        // private

        _validateDependencies: function () {
            Utils.validateNamespace(
                window,
                ['lpTag', 'taglets', 'fileupload'],
                'upload component dependencies not met, aborting instantiation');
        },

        _initModel: function (options) {
            this.logger.info('instantiating model', 'FileUploadInput:_initModel:start');
            this.model = FileUploadInputModelFactory.create(options);
            this.logger.info('instantiated model', 'FileUploadInput:_initModel:end', this.model);
        },

        _initFileUpload: function () {
            this.ui.frameContainer = !_.isUndefined(this.model.get("frameContainerClassName")) ? '.' + this.model.get("frameContainerClassName") : '.' + Config.fileUploadTransport.frameContainerClassName;
            var fileUpload = window.lpTag.taglets.fileupload;
            this._configureFileUpload(fileUpload);
            this._setupFileUploadClient(fileUpload);
        },

        _configureFileUpload: function (fileUpload) {
            this.logger.info('configuring file upload transport for upload ui component', 'FileUploadInput:_configureFileUpload');

            fileUpload.configure({

                uploadExtensions: this.model.getUploadExtensions(true),
                uploadAutoStart: Config.fileUploadTransport.uploadAutoStart,
                iFrameLoadTimeout: this.model.getIFrameLoadTimeout()
            });
        },

        _setupFileUploadClient: function (fileUpload) {
            this.logger.info('configuring file upload transport client for upload ui component', 'FileUploadInput:_setupFileUploadClient');

            this.uploadClient = fileUpload.displayUpload({

                url: LEResourceResolver.getUrl(LEResourceResolver.apiResources.fileUploadCallee),
                uploadUrl: this.model.getUploadUrl(),
                authTokenUrlParName: Config.fileUploadTransport.authTokenUrlParamName,
                token: this.sessionToken,
                uploadFrameDebug: Config.fileUploadTransport.uploadFrameDebug,
                frameClassName: Config.fileUploadTransport.frameClassName,
                uploadInputName: Config.fileUploadTransport.uploadInputName, // should always be 'file', backend depends on it
                frameContainer: this.ui.frameContainer,
                iFrameDelayLoad: 100,

                nativeStyles: this.model.isNativeStyles(),
                frameTitle: this.model.getIFrameTitle(),
                chooseButtonText: this.model.getButtonText() || this.$el.text(),
                chooseButtonStyle: this.model.getButtonStyle(),
                uploadTimeout: this.model.getUploadTimeout(),

                error: _.bind(this._onError, this),
                success: _.bind(this._onInitSuccess, this),
                onUploadStart: _.bind(this._onUploadStart, this),
                onUploadComplete: _.bind(this._onUploadComplete, this),
                onFileSelectionChange: _.bind(this._onFileSelectionChange, this),

                showIframeContent: true
            });

        },

        _onFileSelectionChange: function (data) {
            this.logger.info('file selection change event triggered', 'FileUploadInput:_onFileSelectionChange', data);
            this._updateFileUploadLabel(data);
            this.trigger(Config.events.fileSelectionChange, data);
        },

        _onUploadStart: function (data) {
            this.logger.info('upload start event triggered', 'FileUploadInput:_onUploadStart', data);
            this.trigger(Config.events.uploadStart, data);
        },

        _onUploadComplete: function (data) {
            this.logger.info('upload complete event triggered', 'FileUploadInput:_onUploadComplete', data);
            this.trigger(Config.events.uploadComplete, data);
        },

        _onInitSuccess: function (data) {
            this.logger.info('upload success event triggered', 'FileUploadInput:_onInitSuccess', data);
            this._closeIFrameLoader();
            this.trigger(Config.events.initSuccess, data);
        },

        _onError: function (data) {
            this.logger.error('upload error event triggered', 'FileUploadInput:_onError', data);
            this.logger.graph({ name: "UIComponents:FileUploadInput:_onError"});
            this.trigger(Config.events.error, data);
        },

        _showIFrameLoader: function () {
            if (!this.loader) {
                this.loader = new Loader({
                    permutation: Loader.PERMUTATION.D,
                    el: this.ui.frameContainer
                });
                this.loader.render();
            }
        },

        _closeIFrameLoader: function () {
            if (this.loader) {
                this.loader.close();
                this.loader = null;
            }
        },

        _updateFileUploadLabel: function (data) {
            if (data.state !== Config.fileUploadTransport.responseState.SUCCESS) {
                this.logger.error('file selection change was not successful, cannot update file upload label', 'FileUploadInput:_updateFileUploadLabel', data);
                this.logger.graph({ name: "UIComponents:FileUploadInput:updateFileUploadLabelErr"});
                return;
            }
            this._setFileUploadLabelProperties(data);
        },

        _setFileUploadLabelProperties: function (data) {
            this._setFileUploadLabelText(data);
            this._setFileUploadLabelCss(data);
        },

        _setFileUploadLabelText: function (data) {
            var msg = this._isFileChosen(data) ? data.file.name : this.model.getNoFileChosenMessage();
            this.setFileInputLabelText(msg);
        },

        _setFileUploadLabelCss: function (data) {
            if (this._isFileChosen(data)) {
                this.setFileInputLabelState(Config.FILE_INPUT_STATES.EXISITING_FILE);
                //this.ui.uploadLabel.removeClass(Config.noFileChosenClassName);
            } else {
                this.setFileInputLabelState(Config.FILE_INPUT_STATES.NON_EXISITING_FILE);
                //this.ui.uploadLabel.addClass(Config.noFileChosenClassName);
            }
        },

        _isFileChosen: function (data) {
            return data && data.file && data.file.name;
        },

        _setErrorBubbleTargetElement: function () {
            this.errorBubbleTarget = this.model.getPermutation() === Config.permutation.NATIVE ? this.ui.wrapper : this.ui.uploadLabel;
        },

        _initError: function (msg) {
            this._setErrorOptions(msg);
            this._setErrorCss();
        },

        _destroyError: function () {
            this._clearErrorCss();
            this._clearErrorOptions();
            return true;
        },

        _setErrorCss: function () {
            if (this._isErrorOptionsValid()) {
                this.errorOptions.uiBinding.addClass(Config.errorClassName);
            }
        },

        _clearErrorCss: function () {
            if (this._isErrorOptionsValid()) {
                this.errorOptions.uiBinding.removeClass(Config.errorClassName);
            }
        },

        _setErrorOptions: function (msg) {
            this.errorOptions = {
                text: msg,
                uiBinding: this.errorBubbleTarget
            };
        },

        _clearErrorOptions: function () {
            this.errorOptions = null;
        },

        _createErrorBubble: function () {
            if (!this.uiComponents.hasOwnProperty(Config.errorBubbleId)) {
                this.uiComponents.host$.createAndRender(Config.errorBubbleId, {
                    type: Bubble,
                    options: {
                        targetControl: this.errorOptions.uiBinding,
                        text: this.errorOptions.text,
                        position: this.model.getErrorPosition(),
                        icon: Bubble.ICON.ERROR,
                        showCloseButton: false
                    }
                });
            }
        },

        _destroyErrorBubble: function () {
            if (this.uiComponents.hasOwnProperty(Config.errorBubbleId)) {
                this.uiComponents[Config.errorBubbleId].close();
            }
        },

        _showErrorBubble: function () {
            if (this._isErrorOptionsValid()) {
                this._createErrorBubble();
            }
        },

        _hideErrorBubble: function () {
            if (this._isErrorOptionsValid()) {
                this._destroyErrorBubble();
            }
        },

        _isErrorOptionsValid: function () {
            return this.errorOptions && this.errorOptions.text && _.isString(this.errorOptions.text) && this.errorOptions.uiBinding;
        },

        _setPermutationClasses: function() {
            this.ui.wrapper.toggleClass(BUTTON_ON_RIGHT_CLASSNAME, this.model.isButtonOnRight());
            this.ui.wrapper.toggleClass(NO_TEXT_BUTTON_CLASSNAME, this.model.isButtonTextEmpty());
        }

    });


    // constants

    FileUploadInput.EVE = Config.events;

    FileUploadInput.PERMUTATION = Config.permutation;

    FileUploadInput.FILE_INPUT_STATES = Config.FILE_INPUT_STATES;

    FileUploadInput.ERROR_POSITION = Bubble.POSITION;

    return FileUploadInput;
});
